// This page works as an authentication toolbox and gateway between the App and the Auth Service.
// Any page that may need authentication tools or user credentials, 
// must set this page as a parent (in Router.js)

import React, { useEffect, useState, createContext } from 'react';
import { useKeycloak } from '@react-keycloak/web'
import store from '../../redux/store'
import { expireSession, get_user_settings_from_database_and_save_user_to_cache } from './SessionService';
import { checkPermissions } from '../../helpers/checkPermissions';
import i18n from '../../localization/i18next';
import { clearMobileBootstrapCookies } from './mobileBootstrap';
import { read_niffler_admin_path } from '../../router/niffler_routes';
import { get_auth_language } from '../../helpers/initMenuOptions';

const protocol = process.env.REACT_APP_ERISED_FRONTEND_PROTOCOL;
const url = process.env.REACT_APP_ERISED_FRONTEND_HOST;
const port = process.env.REACT_APP_ERISED_FRONTEND_PORT;

const clients = require('../../config/index').config
const client = process.env.REACT_APP_CLIENT;

const authClient = process.env.REACT_APP_AUTH_CLIENT;

export const AuthContext = createContext();

const read_unique_roles = (roles = []) => {
    return [...new Set(roles.filter(Boolean))];
};

const read_role_values = (roles) => {
    if (Array.isArray(roles)) {
        return roles;
    }

    if (typeof roles === 'string') {
        return roles.split(',').map((role) => role.trim()).filter(Boolean);
    }

    return [];
};

const read_token_roles = (keycloak, clientName) => {
    const realmRoles = read_role_values(keycloak?.tokenParsed?.realm_access?.roles);
    const clientRoles = read_role_values(keycloak?.tokenParsed?.resource_access?.[clientName]?.roles);

    return [
        ...realmRoles,
        ...clientRoles
    ];
};

const read_auth_roles = (user = {}, keycloak, clientName) => {
    return read_unique_roles([
        ...read_role_values(user?.roles),
        ...read_token_roles(keycloak, clientName)
    ]);
};

////////////////////////////////////////////////////////////////////
// Performs authentication before proceeding, loads user credentials
////////////////////////////////////////////////////////////////////
export const GuardedRoute = ({children, ...props}) =>  {

    const { keycloak } = useKeycloak();
    const [ loadedUser, showSpinner] = useState({auth: false, session: false})
    const lang = get_auth_language(i18n.language);

    let currentUrl = window.location.pathname;
    let userCached = store.getState()?.user?.user;

    const generate_redirect_link = (clientName) => {
        return(`${clients[clientName]}${currentUrl}`)
    }

    // Redirects to central app for login. After login, returns to client of choice.
    // Queryparams will be forwarded to client and each client decides how to use them.
    const login = (clientName, redirectUri) => {  

        // If no client is specified, after login we will stay on central application
        if (!clientName) clientName = authClient;
        if (!redirectUri) redirectUri = clients[authClient];

        // deletes user credentials from cache
        expireSession();

        window.location.assign(`${clients[clientName]}/${lang}/auth/?redirectUri=${redirectUri}`);
    }

    const logout = (clientName) => {

        if (!clientName) clientName = authClient;

        // deletes user credentials from cache
        expireSession();
        clearMobileBootstrapCookies();
        sessionStorage.clear();
        localStorage.clear();

        let logoutUrl = keycloak.createLogoutUrl({redirectUri : clients[authClient],
                                                locale: lang});
        window.location.assign(logoutUrl);
    }

    const register = () => {

        // deletes user credentials from cache
        expireSession();

        let registerUrl = keycloak.createLoginUrl({action:'register', redirectUri : generate_redirect_link(),
                                                locale: lang})
        window.location.assign(registerUrl)
    }

    const check_permissions = (roles) => {

        if (!checkPermissions(props.role, roles)) {
            window.location.assign(`${clients[client]}${read_niffler_admin_path(lang, 'access-denied')}`);
        }
    }

    useEffect(() => {
        if(!keycloak.authenticated && props.shouldRedirect) {
            login(authClient, window?.location?.href)
        }
        keycloak.loadUserInfo().then((user) => {
            const authenticatedUser = {
                ...user,
                roles: read_auth_roles(user, keycloak, authClient)
            };

            if(!user.email_verified) window.location.assign(`${protocol}${url}${port}/${lang}/verify-account?redirectUrl=${generate_redirect_link(client)}`)
            store.dispatch({type:'KEYCLOAK', payload: {user: authenticatedUser, token: keycloak.token}});
            check_permissions(authenticatedUser?.roles);
            get_user_settings_from_database_and_save_user_to_cache(keycloak, authenticatedUser)
                .then(data => showSpinner({auth: keycloak.authenticated, session: data}))
        }).catch(() => {
            expireSession();
            showSpinner({...loadedUser, session: true});
        })
    }, [])

    useEffect(() => {
        
        keycloak.onTokenExpired = () => {
            keycloak.updateToken(-1);
        };
        keycloak.onAuthRefreshSuccess = () => {
            store.dispatch({type:'KEYCLOAK', payload: {user: store.getState().user?.user, token: keycloak.token}})
        }
        
    },[keycloak])
    
    if (!loadedUser.session) return <></>
    else return (<AuthContext.Provider value={
                                        { login: (params) => login(params),
                                        register: () => register(),
                                        logout: () => logout(),
                                        login_with_custom_parameters: (params) => login(params),
                                        authenticated: keycloak.authenticated,
                                        user: loadedUser.session}
                                        }>{children}</AuthContext.Provider>)
}
